import { useEffect, useRef, useState } from "react";
import { isRecordingActive, type RecordingStatus } from "@cuekards/core/recording";

/**
 * The three ways a *tab* can lose a take that a desktop window cannot (web
 * ticket 13, stories 52, 53 and 55).
 *
 * None of these has a desktop counterpart, and none of them is loud by default,
 * which is the problem: a browser closes a tab, throttles a hidden one and lets
 * the screen sleep without asking anybody, and each of the three destroys a take
 * that cannot be shot again.
 *
 * Everything here is a function over ports rather than a lump of `window`
 * calls, for the reason the rest of this package is: the decisions — *when* to
 * guard, *when* a warning is warranted, *when* a lock is worth re-taking — are
 * worth testing and none of them needs a browser. The hook at the bottom is the
 * wiring, and is the only part that names a platform object.
 */

/** Anything a listener can be hung on: `window`, `document`, or a fake. */
export interface Listenable {
  addEventListener(type: string, listener: (event: never) => void): void;
  removeEventListener(type: string, listener: (event: never) => void): void;
}

/** As much of a `beforeunload` event as the guard touches. */
export interface UnloadEvent {
  preventDefault(): void;
  returnValue?: unknown;
}

/**
 * Whether the tab is in front, and where to hear about it changing.
 *
 * One type because two of the three guards below need exactly this pair and
 * neither can use one half without the other: a `visibilitychange` is only ever
 * acted on by asking which way it went. In a browser both halves are `document`;
 * in a test they are a fake and a flag.
 */
export interface TabVisibility {
  /** Where `visibilitychange` arrives. */
  events: Listenable;
  hidden(): boolean;
}

/**
 * Refuse to let the tab close silently while a take is running (story 52).
 *
 * A tab is closed by a stray `Ctrl+W` far more often than a desktop application
 * is quit by accident, and the tab in question is by definition the one being
 * performed in front of. The browser's own "leave site?" dialog is the whole
 * mechanism; what a page gets to decide is *when* it appears, and asking on
 * every navigation is how users learn to dismiss it without reading.
 *
 * Both `preventDefault` and `returnValue` are set because browsers disagree
 * about which one arms the dialog, and have changed their minds about it. The
 * string is not shown — no browser has displayed a page's own wording for years
 * — so there is no message to write.
 *
 * The footage is safe either way: it went to storage as it was captured, and
 * web ticket 13's other half offers it back. This is about not making the user
 * find that out.
 */
export function guardTheTab(
  target: Listenable,
  takeInProgress: () => boolean,
): () => void {
  const onUnload = (event: UnloadEvent): void => {
    if (!takeInProgress()) return;
    event.preventDefault();
    event.returnValue = "";
  };
  target.addEventListener("beforeunload", onUnload);
  return () => target.removeEventListener("beforeunload", onUnload);
}

export interface BackgroundPorts {
  tab: TabVisibility;
  now(): number;
  takeInProgress(): boolean;
  /** Say that the tab has come back, having been away for this long. */
  warn(hiddenMs: number): void;
}

/**
 * Notice a take that was left running in the background (story 53).
 *
 * **The warning is raised on the way back, not on the way out**, and that is
 * forced rather than chosen: a hidden tab has nowhere to show anything. So what
 * this watches for is the *return*, and what it reports is how long the risk was
 * taken for — which is the number that decides whether it mattered.
 *
 * Only a backgrounding that happened during a take is reported. A tab hidden
 * while nothing is rolling is somebody using their computer.
 *
 * The take is not stopped and nothing is undone. Browsers throttle a hidden tab
 * rather than freezing it, and a take that survived is a take; telling the user
 * what they nearly did is the useful action, and killing a good take to make the
 * point is not.
 */
export function watchBackgrounding(ports: BackgroundPorts): () => void {
  let hiddenAt: number | null = null;

  const onChange = (): void => {
    if (ports.tab.hidden()) {
      hiddenAt = ports.takeInProgress() ? ports.now() : null;
      return;
    }
    if (hiddenAt === null) return;
    const away = Math.max(0, ports.now() - hiddenAt);
    hiddenAt = null;
    // Reported even if the take has since ended: the risk was taken, and a take
    // that ended while nobody was looking is exactly the one worth checking.
    ports.warn(away);
  };

  ports.tab.events.addEventListener("visibilitychange", onChange);
  return () =>
    ports.tab.events.removeEventListener("visibilitychange", onChange);
}

/** How long the tab was away, in words rather than milliseconds. */
function describeAway(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1_000));
  if (seconds < 90) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * What to say about a take that ran while the tab was behind something else.
 *
 * Specific about the mechanism, because "something might have gone wrong" is
 * advice nobody can act on and this is advice they can: keep the tab in front.
 */
export function backgroundWarning(hiddenMs: number): string {
  return (
    `This tab spent ${describeAway(hiddenMs)} in the background while ` +
    `recording. Browsers slow down hidden tabs, which can interrupt a take — ` +
    `keep cuekards in front while the camera is rolling. Check the file before ` +
    `you rely on it.`
  );
}

/** As much of a `WakeLockSentinel` as holding one needs. */
export interface WakeLockLike {
  /** True once the browser has taken it back, which it does whenever the page
   * is hidden. */
  readonly released: boolean;
  release(): Promise<void>;
}

export interface WakeLockPorts {
  /** `navigator.wakeLock.request("screen")`. Absent on a browser without the
   * API, which is a browser where the screen may sleep and nothing can be done
   * about it. */
  request?(): Promise<WakeLockLike>;
  tab: TabVisibility;
}

/**
 * Keep the screen awake for the length of a take (story 55).
 *
 * A screensaver or a display timeout during a twenty-minute read is a take with
 * a dark monitor in the middle of it — which for a prompter means the script
 * disappeared, and for a vlogger means the eyeline went with it. The desktop app
 * never had to think about it because a window that is being drawn to counts as
 * activity; a tab has to ask.
 *
 * **The lock is re-taken when the tab comes back**, and that is the part worth
 * having code for rather than a single call. A browser releases a screen wake
 * lock the moment the page is hidden and does not return it — so a take that
 * survived a glance at another window would spend the rest of its length
 * unprotected. It also refuses to *grant* one to a hidden page, which is why the
 * request is gated on visibility rather than retried blindly.
 *
 * A refusal is swallowed. There is no version of "the screen might sleep" worth
 * interrupting a take for, and on a browser with no API at all there is nothing
 * to report.
 */
export function screenWakeLock(ports: WakeLockPorts): {
  /** Whether the screen should be held awake right now. */
  hold(wanted: boolean): void;
  dispose(): void;
} {
  let wanted = false;
  let lock: WakeLockLike | null = null;
  /** Requests and releases are chained, so that a take that starts and ends
   * inside one round trip cannot leave a lock held by nothing. */
  let queue: Promise<void> = Promise.resolve();

  const sync = (): void => {
    queue = queue.then(async () => {
      // The browser takes it back whenever the page is hidden, without asking.
      if (lock?.released) lock = null;

      if (wanted && lock === null && !ports.tab.hidden() && ports.request) {
        try {
          lock = await ports.request();
        } catch {
          // Refused — no API, no permission, or a policy. The take is unaffected
          // and there is nothing for the user to do about it.
        }
        return;
      }
      if (!wanted && lock !== null) {
        const held = lock;
        lock = null;
        try {
          await held.release();
        } catch {
          // Already gone, which is the outcome that was wanted.
        }
      }
    });
  };

  const onVisibility = (): void => sync();
  ports.tab.events.addEventListener("visibilitychange", onVisibility);

  return {
    hold(next: boolean): void {
      if (next === wanted) return;
      wanted = next;
      sync();
    },
    dispose(): void {
      ports.tab.events.removeEventListener("visibilitychange", onVisibility);
      wanted = false;
      sync();
    },
  };
}

/**
 * The platform's own event targets, as the narrow shape above.
 *
 * `window` and `document` have every member of {@link Listenable} and a great
 * deal more, and the cast that says so lives here — at the one place the
 * platform is reached for — rather than spread through the functions using it.
 */
const listenable = (target: EventTarget): Listenable =>
  target as unknown as Listenable;

/** This tab's own visibility, both halves of which are `document`. */
const thisTab = (): TabVisibility => ({
  events: listenable(document),
  hidden: () => document.hidden,
});

/** What the browser offers, where it offers it. */
interface WakeLockNavigator {
  wakeLock?: { request(type: "screen"): Promise<WakeLockLike> };
}

/**
 * The three guards, wired to the tab they are about.
 *
 * Mounted once, above the screens, so that stepping out of the prompter to look
 * at a setting cannot take the guard on a rolling take down with it. The
 * listeners go up once and read the current take through a ref rather than being
 * torn down and rebuilt on every status broadcast — a `beforeunload` handler
 * that is being replaced at the moment the tab is closed is a handler that was
 * not there.
 *
 * Returns the one thing that has to be drawn: the warning about a backgrounded
 * take, and the way to dismiss it.
 */
export function useTakeGuards(status: RecordingStatus): {
  backgrounded: string | undefined;
  dismiss: () => void;
} {
  const [backgrounded, setBackgrounded] = useState<string>();
  const active = isRecordingActive(status.phase);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const takeInProgress = (): boolean => activeRef.current;
    const offUnload = guardTheTab(listenable(window), takeInProgress);
    const offBackground = watchBackgrounding({
      tab: thisTab(),
      now: () => Date.now(),
      takeInProgress,
      warn: (hiddenMs) => setBackgrounded(backgroundWarning(hiddenMs)),
    });
    return () => {
      offUnload();
      offBackground();
    };
  }, []);

  const lock = useRef<ReturnType<typeof screenWakeLock>>();
  useEffect(() => {
    const wakeLock = (navigator as WakeLockNavigator).wakeLock;
    const held = screenWakeLock({
      ...(wakeLock ? { request: () => wakeLock.request("screen") } : {}),
      tab: thisTab(),
    });
    lock.current = held;
    return () => held.dispose();
  }, []);

  useEffect(() => {
    lock.current?.hold(active);
  }, [active]);

  return { backgrounded, dismiss: () => setBackgrounded(undefined) };
}
